import React, { useState, useEffect } from 'react';
import { locationService, Location } from '../services/location';
import LocationModal from '../components/LocationModal';

const Locations: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await locationService.getLocations();
      setLocations(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingLocation(null);
    setIsModalOpen(true);
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingLocation(null);
  };

  const handleModalSave = async () => {
    await loadLocations();
    handleModalClose();
  };

  const handleDelete = async (id: string) => {
    try {
      await locationService.deleteLocation(id);
      await loadLocations();
      setDeleteConfirm(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete location';
      if (err.response?.status === 400 && errorMessage.includes('existing dresses')) {
        setError('Cannot delete this location because it has dresses. Please reassign all dresses to another location first.');
      } else {
        setError(errorMessage);
      }
      setDeleteConfirm(null);
    }
  };

  const handleToggleActive = async (location: Location) => {
    try {
      await locationService.updateLocation(location.id, { isActive: !location.isActive });
      await loadLocations();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update location');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading locations...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Location Management</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage store locations for inventory tracking.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={handleCreate}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add Location
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="mt-6">
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {locations.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-500">No locations found. Add a location to get started.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {locations.map((location) => (
                <li key={location.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {location.name}
                          </h3>
                          {location.address && (
                            <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">
                              {location.address}
                            </p>
                          )}
                          {location.whatsappNumber && (
                            <p className="mt-1 text-sm text-gray-600">
                              WhatsApp: {location.whatsappNumber}
                            </p>
                          )}
                          <p className="mt-1 text-sm text-gray-500">
                            {location.dressCount || 0} dresses at this location
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleActive(location)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                          location.isActive 
                            ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }`}
                      >
                        {location.isActive ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        onClick={() => handleEdit(location)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Edit
                      </button>
                      {deleteConfirm === location.id ? (
                        <>
                          <button
                            onClick={() => handleDelete(location.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(location.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {isModalOpen && (
        <LocationModal
          location={editingLocation}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default Locations;